import { useState, useEffect, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';
import { getIdToken } from '../authToken';

// ─── Data access modes ─────────────────────────────────────────────────────────
// SECURITY: the app authenticates users with Firebase (Google SSO). The Supabase
// anon key is public (it ships in the JS bundle), so reading/writing tables
// directly from the browser with that key is only safe if Row Level Security
// denies the anon role — which means the browser CANNOT read tables directly.
//
// Therefore, when REACT_APP_API_URL is set ("API mode") all table reads/writes go
// through the backend (api/server.js), which verifies the caller's Firebase ID
// token and enforces role/ownership rules before touching the database over a
// privileged connection. The Supabase anon key is then locked out of every table
// (see supabase-lockdown.sql).
//
// When REACT_APP_API_URL is NOT set we fall back to talking to Supabase directly
// with the anon key. This path is only for local development against a Supabase
// project whose RLS has not been locked down — never for production.
const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const useApi = Boolean(API_URL);

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

const hasSupabaseConfig = Boolean(SUPABASE_URL && SUPABASE_ANON_KEY);
// The Supabase client is still used for Gallery image *storage* (a public bucket,
// no PII) even in API mode, so we always create it when configured.
const supabase = hasSupabaseConfig ? createClient(SUPABASE_URL, SUPABASE_ANON_KEY) : null;

if (!supabase && !useApi) {
  // eslint-disable-next-line no-console
  console.error(
    '[DB] No data backend configured. Set REACT_APP_API_URL (production) or ' +
    'REACT_APP_SUPABASE_URL + REACT_APP_SUPABASE_ANON_KEY (local dev).'
  );
}

const GALLERY_BUCKET = 'gallery';

// ─── Backend API helper (API mode) ─────────────────────────────────────────────
async function apiFetch(path, options = {}) {
  const token = await getIdToken();
  if (!token) throw new Error('Not authenticated — please sign in again.');
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = await res.json();
      if (body && body.error) message = body.error;
    } catch { /* non-JSON error body */ }
    throw new Error(message);
  }
  if (res.status === 204) return null;
  return res.json();
}

function requireSupabase() {
  if (!supabase) {
    throw new Error(
      'Supabase is not configured. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.'
    );
  }
  return supabase;
}

// ─── In-memory session cache ──────────────────────────────────────────────────
// All pages share a single in-memory copy of each store. A Supabase fetch only
// runs when the store is accessed for the first time (or after invalidateStore).
// put() and remove() update the cache immediately so components re-render
// without any extra network round-trip.

const _cache  = {};                 // storeName → T[]
const _status = {};                 // storeName → 'idle' | 'loading' | 'ready'
const _listeners = new Set();       // React re-render callbacks

function _notify() {
  _listeners.forEach((fn) => fn());
}

// Internal fetch — populates cache from Supabase; deduplicates concurrent calls
async function _load(store) {
  if (_status[store] === 'loading') return;   // already in flight
  if (_status[store] === 'ready') return;     // already cached

  _status[store] = 'loading';
  _notify();

  try {
    if (useApi) {
      // Backend returns an array of { ...payload, id } objects.
      _cache[store] = await apiFetch(`/api/${store}`);
    } else if (!supabase) {
      _cache[store] = [];
    } else {
      const { data, error } = await supabase
        .from(store)
        .select('id, payload, updated_at')
        .order('updated_at', { ascending: false });
      if (error) {
        // eslint-disable-next-line no-console
        console.error(`[DB] load failed for "${store}"`, error.message);
        _cache[store] = [];
      } else {
        _cache[store] = (data || []).map((row) => ({ ...(row.payload || {}), id: row.id }));
      }
    }
    _status[store] = 'ready';
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(`[DB] load error for "${store}"`, err);
    // Leave the store un-cached and mark it idle so the next access retries
    // (e.g. once the auth token becomes available) instead of caching empty
    // data permanently.
    _status[store] = 'idle';
  }

  _notify();
}

// ─── CRUD ─────────────────────────────────────────────────────────────────────

// Returns cached data instantly if available; otherwise fetches from Supabase.
// This means the FIRST page to access a store pays the Supabase latency;
// every other page (and the same page on re-render) gets data instantly.
export async function getAll(store) {
  if (_status[store] === 'ready') return _cache[store] || [];
  await _load(store);
  return _cache[store] || [];
}

// Writes to Supabase and immediately reflects the change in the cache so that
// all subscribed components re-render without waiting for a re-fetch.
export async function put(store, item) {
  if (useApi) {
    // Backend wraps the item into a payload row and enforces auth/ownership.
    await apiFetch(`/api/${store}`, { method: 'POST', body: JSON.stringify(item) });
  } else {
    const sb = requireSupabase();
    const payload = { id: item.id, payload: item, updated_at: new Date().toISOString() };
    const { error } = await sb
      .from(store)
      .upsert(payload, { onConflict: 'id' })
      .select('id')
      .single();
    if (error) throw new Error(error.message);
  }

  // Optimistic cache update — no refetch needed
  if (_status[store] === 'ready') {
    const existing = _cache[store] || [];
    const idx = existing.findIndex((r) => r.id === item.id);
    _cache[store] = idx >= 0
      ? [...existing.slice(0, idx), item, ...existing.slice(idx + 1)]
      : [item, ...existing];
    _notify();
  }

  return item.id;
}

// Admin-only: move a reimbursement invoice through the payout pipeline (e.g.
// mark it paid) and stamp an audit trail on payout. This reuses the standard
// budget upsert route (POST /api/budget) — which admins are already authorised
// to call — so it works against the existing backend with no new endpoint or
// CORS method. The full row is read from cache; since anything already stored
// was inserted under the same 1MB POST cap, re-sending it stays within limits.
// Returns the updated invoice.
export async function setBudgetStatus(id, status, actorEmail) {
  const current = (_cache.budget || []).find((r) => r.id === id);
  if (!current) throw new Error('Invoice not found — reload and try again.');

  const updated = { ...current, status };
  if (status === 'paid') {
    updated.paidAt = new Date().toISOString();
    updated.paidBy = actorEmail || 'admin';
  } else {
    // Reverting out of "paid" clears the payout stamp so it can't mislead.
    delete updated.paidAt;
    delete updated.paidBy;
  }

  // put() handles both API and local Supabase modes, and updates the shared
  // cache so every subscribed page (admin ledger, employee "My Invoices",
  // dashboards) re-renders immediately.
  await put('budget', updated);
  return updated;
}

export async function remove(store, id) {
  if (useApi) {
    await apiFetch(`/api/${store}/${encodeURIComponent(id)}`, { method: 'DELETE' });
  } else {
    const sb = requireSupabase();
    const { error } = await sb.from(store).delete().eq('id', id);
    if (error) throw new Error(error.message);
  }

  // Remove from cache immediately
  if (_status[store] === 'ready') {
    _cache[store] = (_cache[store] || []).filter((r) => r.id !== id);
    _notify();
  }
}

// ─── React hooks ──────────────────────────────────────────────────────────────

// useStore: subscribe to a single store, auto-fetch on first use.
// Replaces the getAll()+useState()+useEffect() pattern in every page.
export function useStore(storeName) {
  const [, setTick] = useState(0);

  useEffect(() => {
    const notify = () => setTick((t) => t + 1);
    _listeners.add(notify);
    _load(storeName);             // no-op if already loading/ready
    return () => _listeners.delete(notify);
  // storeName is stable at each call site — intentionally no dep
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    data: _cache[storeName] || [],
    loading: _status[storeName] !== 'ready',
  };
}

// useStores: subscribe to multiple stores in one call.
// Call sites MUST pass a module-level constant array so the ref is stable.
export function useStores(storeNames) {
  const [, setTick] = useState(0);
  const namesRef = useRef(storeNames);

  useEffect(() => {
    const notify = () => setTick((t) => t + 1);
    _listeners.add(notify);
    namesRef.current.forEach(_load); // kick off any uncached stores
    return () => _listeners.delete(notify);
  // empty deps — storeNames is captured via namesRef on first render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loading = namesRef.current.some((n) => _status[n] !== 'ready');
  return namesRef.current.reduce(
    (acc, name) => { acc[name] = _cache[name] || []; return acc; },
    { loading }
  );
}

// Force one or all stores to be re-fetched from Supabase on next access.
// Useful for a manual "refresh" action or after an external change.
export function invalidateStore(storeName) {
  if (storeName) {
    delete _cache[storeName];
    _status[storeName] = 'idle';
  } else {
    // invalidate everything
    Object.keys(_cache).forEach((k) => { delete _cache[k]; _status[k] = 'idle'; });
  }
  _notify();
  // Re-trigger loads so subscribed components see the refresh immediately
  if (storeName) {
    _load(storeName);
  } else {
    // Re-load all currently subscribed stores
    Object.keys(_status).forEach(_load);
  }
}

// ─── Gallery image storage ─────────────────────────────────────────────────────
export async function uploadGalleryImage(file, id) {
  const sb = requireSupabase();
  const ext = (file.name?.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
  const path = `${id}.${ext}`;
  const { error } = await sb.storage
    .from(GALLERY_BUCKET)
    .upload(path, file, { contentType: file.type || 'image/jpeg', upsert: true });
  if (error) {
    throw new Error(
      error.message?.toLowerCase().includes('bucket')
        ? `Storage bucket "${GALLERY_BUCKET}" not found. Create a public bucket named "${GALLERY_BUCKET}" in Supabase → Storage.`
        : `Image upload failed: ${error.message}`
    );
  }
  const { data } = sb.storage.from(GALLERY_BUCKET).getPublicUrl(path);
  return { url: data.publicUrl, path };
}

export async function deleteGalleryImage(path) {
  if (!supabase || !path) return;
  try {
    await supabase.storage.from(GALLERY_BUCKET).remove([path]);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[Storage] gallery image delete failed', err.message);
  }
}

export function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2);
}

export function usingSupabase() {
  return Boolean(supabase);
}

export function getSupabaseClient() {
  return supabase;
}

// ─── Auth ─────────────────────────────────────────────────────────────────────
export async function signInWithGoogle() {
  if (!supabase) return { error: new Error('Supabase is not configured') };
  return supabase.auth.signInWithOAuth({
    provider: 'google',
    options: { redirectTo: window.location.origin }
  });
}

export async function signOutAuth() {
  if (!supabase) return { error: null };
  return supabase.auth.signOut();
}
